import React, { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, MapPin, Repeat } from "lucide-react";
import { toLocalDateStr } from "./pulseUtils";

const HOURS = Array.from({ length: 15 }, (_, i) => i + 7); // 7 AM to 9 PM
const HOUR_HEIGHT = 60; // px per hour

function formatHour(h) {
  const period = h >= 12 ? "PM" : "AM";
  const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hr} ${period}`;
}

function getWeekDays(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday start
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    return day;
  });
}

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

/**
 * Compute column layout for overlapping events within a single day column.
 * Returns array of { event, top, height, col, totalCols }
 */
function layoutEventsForDay(events) {
  if (!events.length) return [];

  const items = events.map(event => {
    const start = new Date(event.start_datetime);
    const end = new Date(event.end_datetime);
    const startHour = start.getHours() + start.getMinutes() / 60;
    const endHour = end.getHours() + end.getMinutes() / 60;
    return {
      event,
      startHour,
      endHour: Math.max(endHour, startHour + 0.25),
      top: (startHour - 7) * HOUR_HEIGHT,
      height: Math.max((endHour - startHour) * HOUR_HEIGHT, HOUR_HEIGHT * 0.333),
    };
  }).sort((a, b) => a.startHour - b.startHour || a.endHour - b.endHour);

  const clusters = [];
  let currentCluster = [items[0]];
  for (let i = 1; i < items.length; i++) {
    const clusterEnd = Math.max(...currentCluster.map(c => c.endHour));
    if (items[i].startHour < clusterEnd) {
      currentCluster.push(items[i]);
    } else {
      clusters.push(currentCluster);
      currentCluster = [items[i]];
    }
  }
  clusters.push(currentCluster);

  const result = [];
  for (const cluster of clusters) {
    const columns = [];
    for (const item of cluster) {
      let placed = false;
      for (let c = 0; c < columns.length; c++) {
        const lastInCol = columns[c][columns[c].length - 1];
        if (item.startHour >= lastInCol.endHour) {
          columns[c].push(item);
          placed = true;
          break;
        }
      }
      if (!placed) columns.push([item]);
    }
    const totalCols = columns.length;
    columns.forEach((col, colIdx) => {
      col.forEach(item => result.push({ ...item, col: colIdx, totalCols }));
    });
  }
  return result;
}

export const PulseWeekView = ({ currentDate, selectedDate, events, onNavigateWeek, onGoToToday, onSelectDate, onEventClick, onSlotClick, onEventDrop }) => {
  const { t } = useTranslation();
  const [dragOverSlot, setDragOverSlot] = useState(null);

  const weekDays = useMemo(() => getWeekDays(currentDate), [currentDate]);

  const weekLabel = useMemo(() => {
    const start = weekDays[0];
    const end = weekDays[6];
    const startMonth = t(`pulsePlanner.monthsShort.${start.getMonth() + 1}`);
    const endMonth = t(`pulsePlanner.monthsShort.${end.getMonth() + 1}`);
    if (startMonth === endMonth) {
      return `${startMonth} ${start.getDate()} – ${end.getDate()}, ${start.getFullYear()}`;
    }
    return `${startMonth} ${start.getDate()} – ${endMonth} ${end.getDate()}, ${end.getFullYear()}`;
  }, [weekDays, t]);

  const getEventsForDate = useCallback((date) => {
    const dateStr = toLocalDateStr(date);
    return events.filter(e => toLocalDateStr(new Date(e.start_datetime)) === dateStr);
  }, [events]);

  const isToday = (date) => date.toDateString() === new Date().toDateString();
  const isSelected = (date) => date.toDateString() === selectedDate.toDateString();
  const nowHour = new Date().getHours();
  const nowMinute = new Date().getMinutes();

  const handleSlotClick = (date, hour) => {
    const d = new Date(date);
    d.setHours(hour, 0, 0, 0);
    if (onSlotClick) onSlotClick(d);
  };

  const handleDragStart = (e, event) => {
    e.stopPropagation();
    e.dataTransfer.setData("text/plain", JSON.stringify({
      eventId: event.id,
      startDatetime: event.start_datetime,
      endDatetime: event.end_datetime,
    }));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, date, hour) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverSlot(`${toLocalDateStr(date)}-${hour}`);
  };

  const handleDrop = (e, date, hour) => {
    e.preventDefault();
    setDragOverSlot(null);
    try {
      const data = JSON.parse(e.dataTransfer.getData("text/plain"));
      if (!data.eventId) return;
      const oldStart = new Date(data.startDatetime);
      const oldEnd = new Date(data.endDatetime);
      const duration = oldEnd - oldStart;
      const newStart = new Date(date);
      newStart.setHours(hour, 0, 0, 0);
      const newEnd = new Date(newStart.getTime() + duration);
      if (onEventDrop) onEventDrop(data.eventId, newStart, newEnd);
    } catch {}
  };

  const isRecurring = (event) => event.id?.includes("_r");

  return (
    <div className="space-y-4" data-testid="week-view">
      {/* Week Controls */}
      <div className="flex items-center justify-between bg-white border border-slate-200 rounded-sm p-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onNavigateWeek(-1)} className="rounded-sm h-8 w-8 p-0">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onGoToToday} className="rounded-sm h-8 px-3">{t("pulsePlanner.today")}</Button>
          <Button variant="outline" size="sm" onClick={() => onNavigateWeek(1)} className="rounded-sm h-8 w-8 p-0">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <h2 className="font-display text-base font-semibold text-slate-900 uppercase tracking-tight">{weekLabel}</h2>
      </div>

      {/* Week Grid */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {/* Day Headers */}
        <div className="grid grid-cols-[56px_repeat(7,1fr)] border-b border-slate-200">
          <div className="bg-slate-50" />
          {weekDays.map((day, i) => (
            <div
              key={i}
              onClick={() => onSelectDate(day)}
              className={`p-2 text-center cursor-pointer border-l border-slate-100 transition-colors ${
                isSelected(day) ? "bg-indigo-50" : "bg-slate-50 hover:bg-slate-100"
              }`}
            >
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{t(`pulsePlanner.daysShortUpper.${DAY_KEYS[i]}`)}</p>
              <p className={`text-lg font-bold mt-0.5 ${
                isToday(day) ? "w-8 h-8 mx-auto bg-indigo-600 text-white rounded-full flex items-center justify-center" :
                isSelected(day) ? "text-indigo-600" : "text-slate-700"
              }`}>{day.getDate()}</p>
            </div>
          ))}
        </div>

        {/* Time Grid */}
        <div className="overflow-y-auto max-h-[520px]">
          <div className="grid grid-cols-[56px_repeat(7,1fr)] relative">
            {/* Hour Labels */}
            <div className="relative">
              {HOURS.map(hour => (
                <div key={hour} style={{ height: HOUR_HEIGHT }} className="flex items-start justify-end pr-2 pt-0 border-b border-slate-50">
                  <span className="text-[10px] text-slate-400 font-mono -mt-1.5">{formatHour(hour)}</span>
                </div>
              ))}
            </div>

            {/* Day Columns */}
            {weekDays.map((day, dayIdx) => {
              const dayEvents = getEventsForDate(day);
              const todayLine = isToday(day);

              return (
                <div key={dayIdx} className="relative border-l border-slate-100">
                  {/* Hour slots */}
                  {HOURS.map(hour => (
                    <div
                      key={hour}
                      style={{ height: HOUR_HEIGHT }}
                      className={`border-b border-slate-50 cursor-pointer transition-colors ${
                        dragOverSlot === `${toLocalDateStr(day)}-${hour}` ? "bg-indigo-100/50" : "hover:bg-slate-50/80"
                      }`}
                      onClick={() => handleSlotClick(day, hour)}
                      onDragOver={(e) => handleDragOver(e, day, hour)}
                      onDragLeave={() => setDragOverSlot(null)}
                      onDrop={(e) => handleDrop(e, day, hour)}
                    />
                  ))}

                  {/* Current time indicator */}
                  {todayLine && nowHour >= 7 && nowHour <= 21 && (
                    <div
                      className="absolute left-0 right-0 z-20 pointer-events-none"
                      style={{ top: (nowHour - 7 + nowMinute / 60) * HOUR_HEIGHT }}
                    >
                      <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-red-500 -ml-1" />
                        <div className="flex-1 h-[2px] bg-red-500" />
                      </div>
                    </div>
                  )}

                  {/* Events — laid out in side-by-side columns */}
                  {layoutEventsForDay(dayEvents).map(({ event, top, height, col, totalCols }) => {
                    const isRec = isRecurring(event);
                    if (top < 0 || top > HOURS.length * HOUR_HEIGHT) return null;
                    const start = new Date(event.start_datetime);
                    const end = new Date(event.end_datetime);
                    const timeStr = `${start.getHours().toString().padStart(2, "0")}:${start.getMinutes().toString().padStart(2, "0")} – ${end.getHours().toString().padStart(2, "0")}:${end.getMinutes().toString().padStart(2, "0")}`;
                    const colWidth = `calc((100% - 4px) / ${totalCols})`;
                    const colLeft = `calc(1px + (100% - 4px) / ${totalCols} * ${col})`;

                    return (
                      <div
                        key={event.id}
                        draggable={!isRec}
                        onDragStart={!isRec ? (e) => handleDragStart(e, event) : undefined}
                        onClick={(e) => { e.stopPropagation(); if (onEventClick) onEventClick(event); }}
                        className={`absolute z-10 rounded-sm px-1.5 py-1 overflow-hidden border-l-3 border-l-indigo-500 bg-indigo-50 hover:bg-indigo-100 transition-colors ${
                          !isRec ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
                        }`}
                        style={{ top, height: Math.max(height, 22), left: colLeft, width: colWidth }}
                        title={`${event.title} (${timeStr})${isRec ? ` - ${t("pulsePlanner.recurring")}` : ` - ${t("pulsePlanner.dragToReschedule")}`}`}
                      >
                        <p className="text-[10px] font-semibold text-indigo-800 truncate leading-tight">{event.title}</p>
                        {height > 30 && (
                          <p className="text-[9px] text-indigo-500 font-mono truncate">{timeStr}</p>
                        )}
                        {height > 45 && event.location && (
                          <p className="text-[9px] text-indigo-400 truncate flex items-center gap-0.5">
                            <MapPin className="w-2.5 h-2.5" />{event.location}
                          </p>
                        )}
                        {isRec && height > 30 && (
                          <Repeat className="w-2.5 h-2.5 text-indigo-400 absolute top-1 right-1" />
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
